import i18n from "intl/i18n"
import { Guid, IApiLink } from "@/types"

export enum DocumentCategoryUsageScope {
    all = "all",
    assets = "assets",
    requests = "requests",
}

export enum ReservedDocumentCategoryType {
    orderAcknowledgement = "orderAcknowledgement",
    calculations = "calculations",
    deliveryNotes = "deliveryNotes",
    confirmationsOfReceipt = "confirmationsOfReceipt",
    loadingLists = "loadingLists",
    inspections = "inspections",
    pickingLists = "pickingLists",
    rentalContracts = "rentalContracts",
}

export interface ICreateDocumentCategoryDto {
    name: string
    usageScope: DocumentCategoryUsageScope
}

export interface ICreateDocumentCategory {
    dto: ICreateDocumentCategoryDto
    createLink: IApiLink
}

export interface IGetDocumentCategoryDto {
    id: Guid
    name: string
    usageScope?: DocumentCategoryUsageScope
    reservedType?: ReservedDocumentCategoryType | null
}

export default class DocumentCategory {
    id: Guid
    #name: string
    #reservedType?: ReservedDocumentCategoryType
    usageScope?: DocumentCategoryUsageScope

    constructor(dto: IGetDocumentCategoryDto) {
        this.id = dto.id
        this.#name = dto.name
        this.#reservedType = dto.reservedType ?? undefined
        this.usageScope = dto.usageScope
    }

    get name(): string {
        return getReservedDocumentCategoryName(this.#reservedType) || this.#name
    }

    get emptyPlaceholder(): string {
        return getReservedDocumentCategoryEmptyPlaceholder(this.#reservedType)
    }

    get isReserved(): boolean {
        return !!this.#reservedType
    }
}

export function getDocumentCategoryUsageScopeLabel(usageScope?: DocumentCategoryUsageScope): string {
    switch (usageScope) {
        case DocumentCategoryUsageScope.all:
            return i18n.t("documentCategories.usageScope.all")
        case DocumentCategoryUsageScope.assets:
            return i18n.t("documentCategories.usageScope.assets")
        case DocumentCategoryUsageScope.requests:
            return i18n.t("documentCategories.usageScope.requests")
        default:
            return ""
    }
}

function getReservedDocumentCategoryName(reservedType?: ReservedDocumentCategoryType) {
    switch (reservedType) {
        case ReservedDocumentCategoryType.orderAcknowledgement:
            return i18n.t(`documentCategories.reserved.orderAcknowledgement.name.${i18n.language}`)
        case ReservedDocumentCategoryType.calculations:
            return i18n.t(`documentCategories.reserved.calculations.name.${i18n.language}`)
        case ReservedDocumentCategoryType.deliveryNotes:
            return i18n.t(`documentCategories.reserved.deliveryNotes.name.${i18n.language}`)
        case ReservedDocumentCategoryType.confirmationsOfReceipt:
            return i18n.t(`documentCategories.reserved.confirmationsOfReceipt.name.${i18n.language}`)
        case ReservedDocumentCategoryType.loadingLists:
            return i18n.t(`documentCategories.reserved.loadingLists.name.${i18n.language}`)
        case ReservedDocumentCategoryType.inspections:
            return i18n.t(`documentCategories.reserved.inspections.name.${i18n.language}`)
        case ReservedDocumentCategoryType.pickingLists:
            return i18n.t(`documentCategories.reserved.pickingLists.name.${i18n.language}`)
        case ReservedDocumentCategoryType.rentalContracts:
            return i18n.t(`documentCategories.reserved.rentalContracts.name.${i18n.language}`)
        default:
            return ""
    }
}

function getReservedDocumentCategoryEmptyPlaceholder(reservedType?: ReservedDocumentCategoryType) {
    switch (reservedType) {
        case ReservedDocumentCategoryType.orderAcknowledgement:
            return i18n.t("documentCategories.reserved.orderAcknowledgement.empty")
        case ReservedDocumentCategoryType.calculations:
            return i18n.t("documentCategories.reserved.calculations.empty")
        case ReservedDocumentCategoryType.deliveryNotes:
            return i18n.t("documentCategories.reserved.deliveryNotes.empty")
        case ReservedDocumentCategoryType.loadingLists:
            return i18n.t("documentCategories.reserved.loadingLists.empty")
        case ReservedDocumentCategoryType.inspections:
            return i18n.t("documentCategories.reserved.inspections.empty")
        case ReservedDocumentCategoryType.pickingLists:
            return i18n.t("documentCategories.reserved.pickingLists.empty")
        case ReservedDocumentCategoryType.confirmationsOfReceipt:
            return i18n.t("documentCategories.reserved.confirmationsOfReceipt.empty")
        case ReservedDocumentCategoryType.rentalContracts:
            return i18n.t("documentCategories.reserved.rentalContracts.empty")
        default:
            return ""
    }
}
